/**
 * Admin "view as customer" mode (read-only). An admin mints a short-lived grant in
 * the admin app and opens this app at /app/org/:orgId#view=<token>. We capture the
 * token from the URL fragment — never the query string, so it's never sent to a
 * server, logged, or left in Referer — stash it in sessionStorage, and strip it from
 * the URL. apiFetch then sends it as the X-FD-Admin-View header on every request; the
 * API authorizes read-only access to that one org. sessionStorage scopes the grant to
 * this tab and clears it when the tab closes.
 */

const KEY = "fd_admin_view";

let captured = false;

/** Capture #view=<token> from the URL once, on app boot. Idempotent. */
export function captureAdminViewFromUrl(): void {
  if (captured) return;
  captured = true;
  if (typeof window === "undefined") return;
  const hash = window.location.hash;
  const m = /[#&]view=([^&]+)/.exec(hash);
  if (!m) return;
  const token = decodeURIComponent(m[1]!);
  try {
    sessionStorage.setItem(KEY, token);
  } catch {
    // sessionStorage unavailable (private mode etc.) — view mode just won't persist.
  }
  // Strip the token so it doesn't linger in history or get copy-pasted out.
  const cleanedHash = hash.replace(/([#&])view=[^&]+/, "$1").replace(/^#$|[#&]$/, "");
  const url = window.location.pathname + window.location.search + (cleanedHash.startsWith("#") ? cleanedHash : "");
  window.history.replaceState(null, "", url);
}

export function getAdminViewToken(): string | null {
  try {
    return sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function isAdminView(): boolean {
  return getAdminViewToken() !== null;
}

export function clearAdminView(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}
